import type { FC, ReactNode } from 'react'
import type { NavBrand, SiteSettings } from '@/types'

import Link from 'next/link'
import { useEffect, useState } from 'react'

import {
    ArrowUpRight,
    Facebook,
    Instagram,
    Linkedin,
    Mail,
    MapPin,
    Phone
} from 'lucide-react'

import { cms } from '@/lib/cms'
import SiteLogo from '@/components/landing/site-logo'
import { siteConfig, navLinks } from '@/constants/site'

interface SiteFooterProps {
    settings: SiteSettings | null
    brands: NavBrand[]
}

const SiteFooter: FC<SiteFooterProps> = ({ settings, brands }): ReactNode => {
    const [year, setYear] = useState<number | null>(null)

    useEffect(() => {
        setYear(new Date().getFullYear())
    }, [])

    const socials = [
        { label: 'Instagram', href: settings?.instagram_url, icon: Instagram },
        { label: 'LinkedIn', href: settings?.linkedin_url, icon: Linkedin },
        { label: 'Facebook', href: settings?.facebook_url, icon: Facebook }
    ].filter(item => Boolean(item.href))

    const hasContact = Boolean(settings?.email || settings?.phone || settings?.address)

    return (
        <footer className='bg-ink text-white'>
            <div className='mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-8'>
                <div className='grid gap-14 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <Link href='/' aria-label='Spalogy home' className='inline-block focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white'>
                            <SiteLogo settings={settings} className='[&_img]:brightness-0 [&_img]:invert' />
                        </Link>

                        {settings?.footer_text && (
                            <p className='mt-6 max-w-sm text-sm leading-relaxed text-white/65'>
                                <span data-mdlfy-cms={cms('site_settings', settings.id, 'footer_text')}>{settings.footer_text}</span>
                            </p>
                        )}

                        {socials.length > 0 && (
                            <ul className='mt-8 flex gap-3'>
                                {socials.map(({ label, href, icon: Icon }) => (
                                    <li key={label}>
                                        <a
                                            href={href ?? '#'}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            aria-label={label}
                                            className='inline-flex size-10 items-center justify-center border border-white/20 text-white/80 transition-colors hover:border-white hover:text-white focus-visible:outline-2 focus-visible:outline-white'
                                        >
                                            <Icon className='size-4' aria-hidden='true' />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className='lg:col-span-2'>
                        <h2 className='eyebrow text-white/60'>Explore</h2>
                        <ul className='mt-6 space-y-3'>
                            {navLinks.map(link => (
                                <li key={link.href}>
                                    <Link href={link.href} className='text-sm text-white/80 transition-colors hover:text-white focus-visible:underline focus-visible:outline-none'>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='lg:col-span-3'>
                        <h2 className='eyebrow text-white/60'>Brands</h2>
                        <ul className='mt-6 grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-1'>
                            {brands.map(brand => (
                                <li key={brand.id}>
                                    <Link href={`/brands/${brand.slug}`} className='text-sm text-white/80 transition-colors hover:text-white focus-visible:underline focus-visible:outline-none'>
                                        <span data-mdlfy-cms={cms('brands', brand.id, 'name')}>{brand.name}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='lg:col-span-3'>
                        <h2 className='eyebrow text-white/60'>Contact</h2>

                        {hasContact && settings
                            ? (
                                <ul className='mt-6 space-y-4 text-sm text-white/80'>
                                    {settings.email && (
                                        <li className='flex items-start gap-3'>
                                            <Mail className='mt-0.5 size-4 shrink-0 text-white/45' aria-hidden='true' />
                                            <a href={`mailto:${settings.email}`} className='break-all hover:text-white focus-visible:underline focus-visible:outline-none'>
                                                <span data-mdlfy-cms={cms('site_settings', settings.id, 'email')}>{settings.email}</span>
                                            </a>
                                        </li>
                                    )}

                                    {settings.phone && (
                                        <li className='flex items-start gap-3'>
                                            <Phone className='mt-0.5 size-4 shrink-0 text-white/45' aria-hidden='true' />
                                            <a href={`tel:${settings.phone.replace(/[^\d+]/g, '')}`} className='hover:text-white focus-visible:underline focus-visible:outline-none'>
                                                <span data-mdlfy-cms={cms('site_settings', settings.id, 'phone')}>{settings.phone}</span>
                                            </a>
                                        </li>
                                    )}

                                    {settings.address && (
                                        <li className='flex items-start gap-3'>
                                            <MapPin className='mt-0.5 size-4 shrink-0 text-white/45' aria-hidden='true' />
                                            <span data-mdlfy-cms={cms('site_settings', settings.id, 'address')} className='whitespace-pre-line'>{settings.address}</span>
                                        </li>
                                    )}
                                </ul>
                            )
                            : (
                                <p className='mt-6 text-sm leading-relaxed text-white/65'>
                                    Tell us about your project and our team will get back to you.
                                </p>
                            )}

                        <Link
                            href='/#contact'
                            className='mt-8 inline-flex items-center gap-2 border border-white/30 px-5 py-3 text-white transition-colors hover:border-white hover:bg-white hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
                        >
                            <span className='eyebrow'>Send an inquiry</span>
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                </div>

                <div className='mt-20 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-white/50 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        <span>&copy; </span>
                        {year !== null && <span suppressHydrationWarning>{year} </span>}
                        {settings
                            ? <span data-mdlfy-cms={cms('site_settings', settings.id, 'site_name')}>{settings.site_name}</span>
                            : <span>{siteConfig.name}</span>}
                        <span>. All rights reserved.</span>
                    </p>

                    <Link href='/privacy' className='hover:text-white focus-visible:underline focus-visible:outline-none'>
                        Privacy policy
                    </Link>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
